import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { Box } from '@cbhq/cds-web/layout';
import {
  Table,
  TableBody,
  TableCell,
  TableCellFallback,
  TableHeader,
  TableRow,
} from '@cbhq/cds-web/tables';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';

import type { AuditLogRowEntry } from '../types';

import {
  AUDIT_LOG_ACTION_CELL_WIDTH,
  AUDIT_LOG_ACTOR_CELL_WIDTH,
  AUDIT_LOG_DATE_CELL_WIDTH,
  AUDIT_LOG_IP_ADDRESS_CELL_WIDTH,
  AuditLogRow,
  ROW_HEIGHT,
} from './AuditLogRow';

export type ActivityTableProps = {
  auditLogs: AuditLogRowEntry[];
  accessibilityLabel?: string;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onAuditLogClick?: (eventId: string) => void;
};

const HEADER_ROW_HEIGHT = 40;

const PREFETCH_OFFSET_PX = 200;
const SKELETON_ROWS = [0, 1, 2, 3];

const STAGGER_STEP_MS = 20;
const MAX_STAGGER_ROWS = 10; // cap so the cascade lead stays <= ~200ms

const rowStyle: CSSProperties = {
  height: ROW_HEIGHT,
};

const tableStyle: CSSProperties = {
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
};

const headerRowStyle: CSSProperties = {
  height: HEADER_ROW_HEIGHT,
};

const actionHeaderTextStyle: CSSProperties = {
  paddingLeft: 17,
};

const firstTableCellWhitespaceStyle = { paddingY: 0, paddingX: 0, paddingStart: 2 } as const;
const tableCellWhitespaceStyle = { paddingY: 0, paddingX: 3 } as const;

// The sentinel needs a real layout box for the IntersectionObserver to detect it.
// An empty <tr> collapses to a degenerate box in a fixed-layout table and never
// re-triggers once it scrolls out of view, so give it a measurable spanning cell
const sentinelCellStyle: CSSProperties = {
  height: 1,
  padding: 0,
  border: 0,
};

const AUDIT_LOG_COLUMN_COUNT = 4;

const messages = defineMessages({
  tableName: {
    id: 'activityTable.tableName',
    defaultMessage: 'Activity table',
    description: 'Accessible label for the workspace activity audit log table',
  },
  actor: {
    id: 'activityTable.actor',
    defaultMessage: 'Actor',
    description: 'Column header for the audit log actor',
  },
  action: {
    id: 'activityTable.action',
    defaultMessage: 'Action',
    description: 'Column header for the audit log action',
  },
  date: {
    id: 'activityTable.date',
    defaultMessage: 'Date',
    description: 'Column header for the audit log date',
  },
  ip: {
    id: 'activityTable.ip',
    defaultMessage: 'IP',
    description: 'Column header for the audit log IP address',
  },
});

export function ActivityTable({
  auditLogs,
  accessibilityLabel,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
  onAuditLogClick,
}: ActivityTableProps) {
  const { formatMessage } = useIntl();

  const resolvedAccessibilityLabel = accessibilityLabel ?? formatMessage(messages.tableName);

  const sentinelRef = useRef<HTMLTableRowElement>(null);

  // Track the row count from the previous commit so only newly appended rows
  // (index >= batchStartIndex) get a staggered reveal delay; reused rows do not re-animate.
  const prevCountRef = useRef(0);
  useEffect(() => {
    prevCountRef.current = auditLogs.length;
  }, [auditLogs.length]);
  const batchStartIndex = prevCountRef.current;

  useEffect(() => {
    if (!onLoadMore || !hasMore || isLoadingMore) return undefined;

    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin: `${PREFETCH_OFFSET_PX}px`, threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onLoadMore]);

  return (
    <Box width="100%" data-testid="activity-table">
      <Table accessibilityLabel={resolvedAccessibilityLabel} tableLayout="fixed" style={tableStyle}>
        <TableHeader>
          <TableRow disableHoverIndicator style={headerRowStyle}>
            <TableCell
              as="th"
              scope="col"
              outerSpacing={firstTableCellWhitespaceStyle}
              width={AUDIT_LOG_ACTOR_CELL_WIDTH}
            >
              <Text font="label1" color="fgMuted">
                {formatMessage(messages.actor)}
              </Text>
            </TableCell>
            <TableCell
              as="th"
              scope="col"
              outerSpacing={tableCellWhitespaceStyle}
              width={AUDIT_LOG_ACTION_CELL_WIDTH}
            >
              <Box width="100%" style={actionHeaderTextStyle}>
                <Text font="label1" color="fgMuted">
                  {formatMessage(messages.action)}
                </Text>
              </Box>
            </TableCell>
            <TableCell
              as="th"
              scope="col"
              outerSpacing={tableCellWhitespaceStyle}
              width={AUDIT_LOG_DATE_CELL_WIDTH}
            >
              <Text font="label1" color="fgMuted">
                {formatMessage(messages.date)}
              </Text>
            </TableCell>
            <TableCell
              as="th"
              scope="col"
              outerSpacing={tableCellWhitespaceStyle}
              width={AUDIT_LOG_IP_ADDRESS_CELL_WIDTH}
            >
              <Text font="label1" color="fgMuted">
                {formatMessage(messages.ip)}
              </Text>
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {auditLogs.map((auditLog, index) => {
            const offset = index - batchStartIndex;
            const staggerSlot = offset > 0 ? Math.min(offset, MAX_STAGGER_ROWS) : 0;
            return (
              <AuditLogRow
                key={auditLog.eventId}
                auditLog={auditLog}
                onAuditLogClick={onAuditLogClick}
                revealDelayMs={staggerSlot * STAGGER_STEP_MS}
              />
            );
          })}
          {isLoadingMore &&
            SKELETON_ROWS.map((index) => (
              <TableRow
                key={`audit-skeleton-${index}`}
                style={rowStyle}
                aria-hidden
                data-testid="audit-skeleton-row"
              >
                <TableCellFallback
                  start="avatar"
                  title
                  subtitle
                  width={AUDIT_LOG_ACTOR_CELL_WIDTH}
                  outerSpacing={firstTableCellWhitespaceStyle}
                  disableRandomRectWidth
                  rectWidthVariant={index}
                />
                <TableCellFallback
                  start="icon"
                  title
                  subtitle
                  width={AUDIT_LOG_ACTION_CELL_WIDTH}
                  outerSpacing={tableCellWhitespaceStyle}
                  disableRandomRectWidth
                  rectWidthVariant={index + 1}
                />
                <TableCellFallback
                  title
                  subtitle
                  width={AUDIT_LOG_DATE_CELL_WIDTH}
                  outerSpacing={tableCellWhitespaceStyle}
                  disableRandomRectWidth
                  rectWidthVariant={index + 2}
                />
                <TableCellFallback
                  title
                  width={AUDIT_LOG_IP_ADDRESS_CELL_WIDTH}
                  outerSpacing={tableCellWhitespaceStyle}
                  disableRandomRectWidth
                  rectWidthVariant={index + 3}
                />
              </TableRow>
            ))}
          {hasMore && !isLoadingMore && (
            <tr
              key="load-more-sentinel"
              ref={sentinelRef}
              aria-hidden
              data-testid="load-more-sentinel"
            >
              <td colSpan={AUDIT_LOG_COLUMN_COUNT} style={sentinelCellStyle} />
            </tr>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
